import { useEffect, useState } from "react";
import styles from "./MyAppsDetail.module.css";
import { CircularProgress } from "@mui/material";
import { IoArrowBackSharp } from "react-icons/io5";
import { FaExternalLinkAlt } from "react-icons/fa";
import { convertDateFromTFormat } from "../../store/Currency_Converters/CurrencyConverter.ts";
import VendorPriceEditor from "./VendorPriceEditor.js";
import ToggleSwitch from "./ToggleSwitch";
import { openAdminSalesOrder } from "../../utils/salesOrderPdf";
import { toast } from "react-toastify";
import { STATUS_DATA } from "../../utils/statusData";

// TLD -> registrar product key (customer-price.json is keyed by product key).
const TLD_PRODUCT_KEYS: Record<string, string> = {
  ".com": "domcno", ".net": "dotnet", ".org": "domorg", ".info": "dotinfo",
  ".biz": "dotbiz", ".co": "dotco", ".xyz": "dotxyz", ".online": "dotonline",
  ".site": "dotsite", ".app": "dotapp", ".store": "dotstore", ".pro": "dotpro",
  ".me": "dotme", ".mobi": "dotmobi", ".in": "dotin", ".co.in": "thirdleveldotin",
  ".org.in": "thirdleveldotorg", ".us": "dotus", ".uk": "dotuk", ".ca": "dotca",
  ".au": "dotau", ".de": "dotde", ".fr": "dotfr", ".jp": "dotjp",
};

interface rowState {
  id: number;
  amount: number;
  amount_default: number;
  coupon: string | null;
  createdAt: string;
  currency: string;
  current_order: number;
  dueDate: string;
  identifier: string | null;
  interval: "year" | "month" | string;
  marketplaceName: string;
  n_interval: number;
  noOfLicences: number;
  onlinePayment: boolean;
  order_ids: number[];
  payment_method: string | null;
  price_id: string | null;
  provider: string | null;
  purchaseDate: string;
  regionId: number;
  required_feilds: { [key: string]: any }[];
  review: string | null;
  skuId: number;
  skuName: string | null;
  status: "active" | "inactive" | string;
  stripe_sub: string;
  unit_ids: number[] | null;
  updatedAt: string;
  work_id: number;
  discount: number | null;
}

interface DomainDetailProps {
  selectedApp: rowState;
  setSelectedApp: (value: any) => void;
  isSomethingChanged?: boolean;
  setIsSomethingChanged?: (value: any) => void;
}

const DomainDetail = ({
  selectedApp,
  setSelectedApp,
  isSomethingChanged,
  setIsSomethingChanged,
}: DomainDetailProps) => {
  // Rendered inline by MyAppsFunctions — same model as the recurring sub
  // detail (MyAppsDetail). Back button clears selectedApp instead of
  // navigating, so the parent keeps its workspace context intact.
  const rowData = selectedApp;
  const statusData: Record<string, string> = STATUS_DATA;
  const [domainInvoices, setDomainInvoices] = useState<any>(null);
  // Full financial history for the domain. These used to be missing entirely:
  // the page showed a single latest payment and no bills/sales orders at all.
  const [pastPayments, setPastPayments] = useState<any[]>([]);
  const [pastBills, setPastBills] = useState<any[]>([]);
  const [soBusy, setSoBusy] = useState<Record<string, boolean>>({});
  // Live registrar renewal price. The upcoming invoice used to be derived from
  // rowData.amount, which is the STORED subscription amount — corrupted on rows
  // the legacy add/remove path touched (it showed a .com-era 5832 for a .in
  // domain). The customer portal already quotes the live price; admin must
  // agree with it.
  const [upcomingRenewalPrice, setUpcomingRenewalPrice] = useState<number | null>(null);
  const [compeleteDataLoader, setCompeleteDataLoader] = useState(true);
  // Offline flow: the renewal/purchase creates a Zoho SO and a DRAFT bill.
  // Payment arrives out-of-band (bank transfer), then an admin marks the bill
  // paid here — pay_onetime_bill converts the SO into a Zoho invoice, marks it
  // paid, and writes the invoice/payment rows. Domains had no Pay control at
  // all, so a draft bill could never be settled except by hand in the DB.
  const [payTarget, setPayTarget] = useState<any>(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const loadBills = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/past_bills`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        method: "POST",
        body: JSON.stringify({
          stripe_sub: (rowData as any).stripe_sub,
          ps_id: (rowData as any).id,
        }),
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json?.data) setPastBills(json.data);
    } catch (err) {
      console.log("bills reload failed", err);
    }
  };

  const payBill = async () => {
    if (!payTarget) return;
    setPaying(true);
    setPayError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/pay_onetime_bill`, {
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        method: "POST",
        body: JSON.stringify({ bill_id: payTarget.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.status === 200) {
        setPayTarget(null);
        await loadBills();
      } else {
        setPayError(data?.msg || "Could not mark this bill as paid.");
      }
    } catch (err: any) {
      setPayError(err?.message || "Network error");
    } finally {
      setPaying(false);
    }
  };

  const runSalesOrder = async (
    soId: string,
    disposition: "inline" | "attachment",
    key: string,
    soNumber?: string
  ) => {
    if (soBusy[key]) return;
    setSoBusy((b) => ({ ...b, [key]: true }));
    try {
      // Surface the reason. Without an onError the helper fails silently and
      // the button is indistinguishable from a dead one.
      await openAdminSalesOrder(soId, disposition, soNumber, (msg: string) =>
        toast.error(msg || "Could not open the sales order."),
      );
    } finally {
      setSoBusy((b) => ({ ...b, [key]: false }));
    }
  };

  useEffect(() => {
    if (rowData) {
    //   const fetchModifyOrders = async () => {
    //     return fetch(`${process.env.REACT_APP_SERVER_URL}/modify_orders`, {
    //       headers: {
    //         Accept: "application/json",
    //         "Content-Type": "application/json",
    //       },
    //       credentials: "include",
    //       method: "POST",
    //       body: JSON.stringify({ price_id: rowData.price_id }),
    //     })
    //       .then((response) => {
    //         if (response.status === 200) {
    //           return response.json();
    //         } else if (response.status === 201) {
    //           console.log("DATA NOT FETCHED");
    //         } else if (response.status === 202) {
    //           navigate("/login");
    //         }
    //       })
    //       .then((data) => {
    //         setPricingData(data.data);
    //       })
    //       .catch((err) => {
    //         console.log(err);
    //       });
    //   };

      const fetchDomainInvoice = async () => {
        fetch(`${process.env.REACT_APP_SERVER_URL}/domain_invoices`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({
            ps_id: (rowData as any).id,
            order_id: rowData.current_order,
          }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            }
          })
          .then((data) => {
            console.log("domain invoicedata", data.invoice);
            setDomainInvoices(data.invoice);
          })
          .catch((err) => {
            console.log(err);
          });
      };

      // Domain subs have no Stripe subscription, so the backend resolves their
      // rows by ps_id (bills/payments carry stripe_sub = the PS id).
      const linkBody = JSON.stringify({
        stripe_sub: (rowData as any).stripe_sub,
        ps_id: (rowData as any).id,
      });
      const postJson = (path: string) =>
        fetch(`${process.env.REACT_APP_SERVER_URL}/${path}`, {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          method: "POST",
          body: linkBody,
        })
          .then((r) => (r.status === 200 ? r.json() : null))
          .catch(() => null);

      const fetchPastPayments = async () => {
        const d = await postJson("past_payments");
        if (d?.data) setPastPayments(d.data);
      };
      const fetchPastBills = async () => {
        const d = await postJson("past_bills");
        if (d?.data) setPastBills(d.data);
      };

      const fetchUpcomingPrice = async () => {
        try {
          const domain = (rowData as any)?.required_feilds?.find(
            (f: any) => f?.name === "domain"
          )?.value as string | undefined;
          if (!domain) return;
          const dot = domain.includes(".")
            ? `.${domain.split(".").slice(1).join(".")}`
            : "";
          const tldKey = (TLD_PRODUCT_KEYS as Record<string, string>)[dot];
          if (!tldKey) return;
          const res = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/get_domain_price`,
            {
              method: "POST",
              credentials: "include",
              headers: { Accept: "application/json", "Content-Type": "application/json" },
            }
          );
          if (!res.ok) return;
          const json = await res.json();
          const domainMap = json?.data?.data ?? json?.data ?? {};
          const renewMap = domainMap?.[tldKey]?.renewdomain;
          if (!renewMap) return;
          const years = String((rowData as any).n_interval || 1);
          const price = Number(renewMap[years]);
          if (!isNaN(price) && price > 0) setUpcomingRenewalPrice(price);
        } catch (err) {
          console.log("upcoming renewal price fetch failed:", err);
        }
      };

      const fetchAllData = async () => {
        setCompeleteDataLoader(true);
        try {
          await Promise.all([
            fetchDomainInvoice(),
            fetchPastPayments(),
            fetchPastBills(),
            fetchUpcomingPrice(),
          ]);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setCompeleteDataLoader(false);
        }
      };

      fetchAllData();
    }
    // eslint-disable-next-line
  }, []);

  const deduct18Percent = (amount: any) => {
    return (amount * (100 / 118)).toFixed(2);
  };

  return (
    <>
      {payTarget && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1300, background: "rgba(0,0,0,.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !paying && setPayTarget(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 16, padding: 24, width: 460, fontFamily: "Poppins" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: 18 }}>Mark bill as paid?</h3>
            <p style={{ color: "#555", fontSize: 14, marginTop: 8 }}>
              This records payment received out-of-band for{" "}
              <strong>
                {payTarget.currency === "INR" ? "₹" : "$"}
                {payTarget.subtotal_default}
              </strong>
              {payTarget.sales_order_number ? ` (${payTarget.sales_order_number})` : ""}.
              The Zoho sales order is converted into an invoice and marked paid,
              and the invoice + payment records are created.
            </p>
            {payError && (
              <p style={{ background: "#fdecea", color: "#b3261e", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>
                {payError}
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <button disabled={paying} onClick={() => setPayTarget(null)} style={{ cursor: "pointer" }}>
                Cancel
              </button>
              <button disabled={paying} onClick={payBill} style={{ cursor: "pointer" }}>
                {paying ? "Processing…" : "Confirm payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!compeleteDataLoader ? (
        <div className={styles.mainContainer}>
          <div className={styles.headingContainer}>
            <div className={styles.headingLeftContainer}>
              <div>
                <IoArrowBackSharp
                  onClick={() => {
                    // Mirror MyAppsDetail (recurring) exactly — clear the
                    // selectedApp in the parent so its workspace context
                    // and subs list re-render. No URL change, no unmount.
                    setSelectedApp([]);
                  }}
                  className={styles.backButton}
                />
              </div>
              <h3>{rowData.marketplaceName}</h3>
              <div
                style={{
                  backgroundColor: statusData[rowData.status] || "#000",
                  color: "white",
                  fontFamily: "Poppins",
                  fontSize: "0.7rem",
                  padding: "2px 6px",
                  borderRadius: "6px",
                  border: statusData[rowData.status] || "#000",
                  marginTop: "0.5rem",
                  marginLeft: "1.5rem",
                  marginBottom: "0.5rem",
                  transition:
                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  display: "inline-block",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {rowData.status}
              </div>
              {/* Online-payment switch, same control the recurring detail has.
                  Domains had none, so turning a workspace offline meant editing
                  the DB by hand. */}
              <ToggleSwitch
                nextOnlinePayment={(rowData as any).onlinePayment}
                api={"online_toggle"}
                id={(rowData as any).id}
                type={"subs"}
                isSomethingChanged={isSomethingChanged}
                setIsSomethingChanged={setIsSomethingChanged}
                setSelectedApp={setSelectedApp}
              />
              {rowData.status === "cancel_next" && (
                <div className={styles.statusDesc}>
                  {" "}
                  Cancels at Interval end
                </div>
              )}
            </div>
          </div>
          <div className={styles.horizontalLine} />

          {/* Vendor-cost editor — same component used in the recurring
              Update Subscription drawer. Pure local mutation, no Stripe. */}
          <VendorPriceEditor subscription={rowData as any} />

          {/* next invoice, auto-cancellation */}

          {/* SUBCRIPTION CONTAINER */}
          <div className={styles.subscriptionContainer}>
            <div className={styles.tableContainer}>
              <div className={styles.tableLeftContainer}>
                <div className={styles.leftColoumn}>
                  <span className={styles.tableTitle}>Created</span>
                  <span className={styles.tableTitle}>Current Period</span>
                  <span className={styles.tableTitle}>Id</span>
                </div>
                <div className={styles.rightColoumn}>
                  <span className={styles.tableDesc}>
                    {" "}
                    {rowData &&
                      rowData?.createdAt
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}
                    ,{" "}
                    {rowData &&
                      rowData?.createdAt
                        .split("T")[1]
                        .split(":")
                        .slice(0, 2)
                        .join(":")}
                  </span>
                  <span className={styles.tableDesc}>
                    {" "}
                    {rowData &&
                      rowData?.createdAt
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}{" "}
                    To{" "}
                    {rowData &&
                      rowData?.dueDate
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}{" "}
                  </span>
                  <span className={styles.tableDesc}>{rowData.stripe_sub}</span>
                </div>
              </div>
              <div className={styles.tableRightContainer}>
                <div className={styles.leftColoumn}>
                  {rowData?.discount && (
                    <span className={styles.tableTitle}>Discounts</span>
                  )}

                  <span className={styles.tableTitle}>Payment Due</span>
                  <span className={styles.tableTitle}>Next Invoice</span>
                </div>
                <div className={styles.rightColoumn}>
                  {rowData?.discount && (
                    <span className={styles.tableDesc}>
                      {rowData?.discount}
                    </span>
                  )}
                  <span className={styles.tableDesc}>
                    {" "}
                    {rowData &&
                      rowData.dueDate
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}{" "}
                  </span>
                  <span className={styles.tableDesc}>
                    {" "}
                    {rowData &&
                      rowData.dueDate
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* REQUIRED FIELD HEADING */}
          <div className={styles.requiredHeadingContainer}>
            <h3>Required Fields</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />

          {/* REQUIRED FIELD CONTAINER */}
          <div className={styles.requiredFieldContainer}>
            <div className={styles.tableContainer}>
              <div className={styles.tableLeftContainer}>
                {rowData &&
                  rowData.required_feilds.length !== 0 &&
                  rowData.required_feilds.map((item, i) => (
                    <>
                      <div className={styles.leftColoumn}>
                        <span className={styles.tableTitle}>{item.title}</span>
                      </div>
                      <div className={styles.rightColoumn}>
                        <span className={styles.tableDesc}>{item.value}</span>
                      </div>
                    </>
                  ))}
                {rowData && rowData.required_feilds.length === 0 && (
                  <p>No data</p>
                )}
              </div>
            </div>
          </div>

          {/* UPCOMING INVOICE HEADING */}
          <div className={styles.upComingInvoiceHeadingContainer}>
            <h3>UpComing Invoice</h3>
            <p>
              This is a preview of the invoice that will be billed on{" "}
              <span style={{ fontWeight: "bold" }}>
                {rowData?.dueDate?.split("T")[0].split("-").reverse().join("-")}
              </span>
              . It may Change if the subscription is updated.
            </p>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />

          {/* UPCOMING INVOICE WHOLE CODE FOR  EASEXPENSE DOMAINS */}
          {rowData.marketplaceName === "Easexpense Domains" && (
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Description
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Qty
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Unit Price
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.cell}>{rowData.marketplaceName}</div>
                <div className={styles.cell}>1</div>
                <div className={styles.cell}>
                  {" "}
                  {rowData.currency === "INR" ? "₹" : "$"}
                  {upcomingRenewalPrice !== null
                    ? upcomingRenewalPrice.toFixed(2)
                    : deduct18Percent(rowData.amount)}
                </div>
                <div className={styles.cell}>
                  {" "}
                  {rowData.currency === "INR" ? "₹" : "$"}
                  {upcomingRenewalPrice !== null
                    ? upcomingRenewalPrice.toFixed(2)
                    : deduct18Percent(rowData.amount)}
                </div>
              </div>
            </div>
          )}

          {/* ONLY INVOICES HEADING */}
          <div className={styles.priceHeadingContainer}>
            <h3>Invoices</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />

          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Link
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>

              {/* Every payment, not just the latest one — a domain that has
                  been renewed has one row per term. */}
              {(pastPayments.length
                ? pastPayments
                : domainInvoices
                ? [domainInvoices]
                : []
              ).map((item: any, idx: number) => (
                <div className={styles.row} key={item?.id ?? idx}>
                  <div className={styles.cell}>
                    {item?.currency === "INR" ? "₹" : "$"}
                    {item?.amount}
                  </div>
                  <div className={styles.cell}>
                    {item?.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div className={styles.cell}>{item?.status || "—"}</div>
                  <div className={styles.cell}>
                    {convertDateFromTFormat(item?.updatedAt)}{" "}
                  </div>
                </div>
              ))}
              {!pastPayments.length && !domainInvoices && (
                <div className={styles.row}>
                  <div className={styles.cell}>No payments recorded.</div>
                </div>
              )}
            </div>
          </div>

          {/* BILLS + SALES ORDERS */}
          <div className={styles.priceHeadingContainer}>
            <h3>Bills &amp; Sales Orders</h3>
          </div>
          <div className={styles.horizontalLine} />

          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                {["Bill Id", "Order ID", "Amount", "Type", "Status", "Last Payment", "Sales Order", "Action", "Payment", "Created"].map(
                  (h) => (
                    <div
                      key={h}
                      className={`${styles.cell} ${styles.title}`}
                      style={{ color: "#393939", fontWeight: "550" }}
                    >
                      {h}
                    </div>
                  )
                )}
              </div>

              {pastBills.map((b: any, idx: number) => {
                const viewKey = `${b.id}-view`;
                const dlKey = `${b.id}-dl`;
                return (
                  <div className={styles.row} key={b?.id ?? idx}>
                    <div className={styles.cell}>{b?.id ? `Bill_${b.id}` : "—"}</div>
                    <div className={styles.cell}>{b?.order_id ? `Order_${b.order_id}` : "—"}</div>
                    <div className={styles.cell}>
                      {b?.currency === "INR" ? "₹" : "$"}
                      {b?.subtotal_default}
                    </div>
                    <div className={styles.cell}>{b?.type || "—"}</div>
                    <div className={styles.cell}>{b?.status || "—"}</div>
                    <div className={styles.cell}>{b?.latestPayment || "—"}</div>
                    <div className={styles.cell}>
                      {b?.sales_order_number || b?.sales_order_id || "—"}
                    </div>
                    <div className={styles.cell}>
                      {b?.sales_order_id ? (
                        <>
                          <button
                            type="button"
                            disabled={soBusy[viewKey]}
                            onClick={() =>
                              runSalesOrder(
                                b.sales_order_id,
                                "inline",
                                viewKey,
                                b.sales_order_number
                              )
                            }
                            style={{ marginRight: 8, cursor: "pointer" }}
                          >
                            {soBusy[viewKey] ? "…" : "View"}
                          </button>
                          <button
                            type="button"
                            disabled={soBusy[dlKey]}
                            onClick={() =>
                              runSalesOrder(
                                b.sales_order_id,
                                "attachment",
                                dlKey,
                                b.sales_order_number
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {soBusy[dlKey] ? "…" : "Download"}
                          </button>
                        </>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div className={styles.cell}>
                      {String(b?.status).toLowerCase() === "paid" ? (
                        "Paid"
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setPayError("");
                            setPayTarget(b);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          Mark as Paid
                        </button>
                      )}
                    </div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(b?.createdAt)}
                    </div>
                  </div>
                );
              })}
              {!pastBills.length && (
                <div className={styles.row}>
                  <div className={styles.cell}>
                    No bills recorded for this domain.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={25} />
        </div>
      )}
    </>
  );
};

export default DomainDetail;
